import { useForm } from "react-hook-form";
import {
  UrgentConcernFormModel,
  UrgentConcernModel,
} from "../../../services/api/types/shiftHandover/models/UrgentConcernModel";
import { HandoverSection } from "../molecules/HandoverSection";
import { View } from "ui/atoms/view";
import { NoItemsComponent } from "ui/atoms/noItems";
import { FormControlledInput } from "ui/molecules/form/FormControlledInput";
import { ButtonIcon, Button } from "ui/gluestack/button";
import { UploadCloudIcon } from "ui/gluestack/icon/UploadCloud";
import { useAddUrgentConcernMutation } from "app/services/api/shiftHandoverApi";
import { urgentConcernsApi } from "app/services/api/urgentConcernsApi";
import { useDispatch } from "react-redux";
import { useResidentContext } from "app/features/residents/provider/ResidentProvider";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { Text } from "react-native";
import { PermissionCheck } from "app/features/permission/atoms/PermissionCheck";
import { PermissionsEnums } from "app/services/api/types/authorisation/enums/PermissionsEnums";
import { UrgentConcernRecord } from "app/features/urgentConcerns/molecules/UrgentConcernRecord";

interface Props {
  isEditing?: boolean;
  data: UrgentConcernModel[];
}

export function UrgentTasks({ data, isEditing }: Props) {
  const { residentId } = useResidentContext();
  const [addUrgentConcern] = useAddUrgentConcernMutation();
  const { showErrorToast } = useDisplayToast();
  const dispatch = useDispatch();

  const {
    control,
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<{ concern: UrgentConcernFormModel }>({
    defaultValues: { concern: { description: "" } },
  });

  const submit = handleSubmit(async (data) => {
    try {
      if (!residentId)
        throw new Error(
          "Unable to determine which resident to add concern for",
        );
      await addUrgentConcern({
        body: {
          resident_id: residentId,
          description: data.concern.description,
        },
      }).unwrap();
      dispatch(urgentConcernsApi.util.invalidateTags(["UrgentConcerns"]));
      reset();
    } catch (e) {
      showErrorToast(e);
    }
  });

  return (
    <HandoverSection
      title="Urgent Concerns"
      titleClassNames="text-error-700"
      subtitle="*Enter every urgent concern in separate field."
    >
      <>
        <UrgentConcernRecord
          data={data}
          isEditing={isEditing}
          showTypeBadge
          noDataMessage={
            <NoItemsComponent
              title="No urgent concerns"
              subtitle="There are no listed urgent concerns"
            />
          }
        />

        {isEditing ? (
          <PermissionCheck permission={PermissionsEnums.UPDATE_SHIFT_HANDOVER}>
            <View className="mt-8">
              <View className="mb-1">
                <Text>Add concerns or urgent tasks</Text>
              </View>
              <View className="flex-row gap-2 items-start">
                <View className="flex-1">
                  <FormControlledInput
                    id="urgent-concern-form"
                    aria-label="Input an urgent concern"
                    placeholder="Enter your urgent concern"
                    control={control}
                    name="concern.description"
                    onSubmitEditing={submit}
                    isRequired
                    isDisabled={isSubmitting}
                  />
                </View>
                <Button
                  action="positive"
                  variant="outline"
                  onPress={submit}
                  className="mt-1"
                  disabled={isSubmitting}
                >
                  <ButtonIcon as={UploadCloudIcon} />
                </Button>
              </View>
            </View>
          </PermissionCheck>
        ) : null}
      </>
    </HandoverSection>
  );
}
